import React, { useRef, useState } from "react";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Container, SectionLabel, ButtonLux } from "@/components/primitives";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { signIn, completeTwoFactor, user, signOut } = useAuth();
  const [step, setStep] = useState("creds");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [code, setCode] = useState("");
  const twoFactorTokenRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const inputCls = "w-full border-b border-lux-lineStrong bg-transparent py-3 text-[16px] text-lux-ink font-light outline-none focus:border-lux-ink transition-colors";

  const submitCreds = async () => {
    setErr(null); setLoading(true);
    try {
      const r = await signIn(email.trim(), password);
      if ("requires2FA" in r) { twoFactorTokenRef.current = r.twoFactorToken; setStep("2fa"); setCode(""); }
      else setDone(true);
    } catch (e) {
      const s = e?.response?.status;
      setErr(s === 401 ? "Invalid credentials." : s === 403 ? "Account suspended." : "Could not sign in.");
    } finally { setLoading(false); }
  };

  const submitCode = async () => {
    if (!twoFactorTokenRef.current) { setStep("creds"); return; }
    setErr(null); setLoading(true);
    try { await completeTwoFactor(twoFactorTokenRef.current, code.trim()); setDone(true); }
    catch (e) { setErr(e?.response?.status === 401 ? "Invalid code. Try again." : "Could not verify code."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-lux-bg flex flex-col">
      <Seo title="Studio Access · Onedot6" description="Sign in for Onedot6 studio members, partners and management." path="/login" />
      <TopBar />
      <Container className="pt-40 md:pt-52 pb-24 flex-1 max-w-[560px]">
        {done || user ? (
          <div data-testid="login-done">
            <SectionLabel>Signed In</SectionLabel>
            <h1 className="font-serif text-lux-ink font-light tracking-tight leading-none mt-2 text-4xl md:text-6xl">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</h1>
            <p className="text-base text-lux-sec font-light leading-relaxed mt-5">
              You’re authenticated. Content is managed from the Onedot6 studio (CMS) app. This website is the public storefront.
            </p>
            <div className="mt-8 flex gap-4">
              <ButtonLux label="Back to Site" to="/" testId="login-home" />
              <ButtonLux label="Sign Out" variant="outline" onClick={async () => { await signOut(); setDone(false); setEmail(""); setPassword(""); setStep("creds"); }} testId="login-signout" />
            </div>
          </div>
        ) : step === "creds" ? (
          <>
            <SectionLabel>Studio Access</SectionLabel>
            <h1 className="font-serif text-lux-ink font-light tracking-tight leading-none mt-2 text-5xl md:text-7xl">Sign in.</h1>
            <p className="text-base text-lux-sec font-light mt-4">For Onedot6 studio members, partners and management.</p>
            <div className="h-10" />
            <label className="block text-[11px] tracking-[0.15em] uppercase text-lux-muted mb-2">Email</label>
            <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" className={inputCls} />
            <div className="h-6" />
            <label className="block text-[11px] tracking-[0.15em] uppercase text-lux-muted mb-2">Password</label>
            <input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="current-password" onKeyDown={(e) => e.key === "Enter" && submitCreds()} className={inputCls} />
            <div className="flex items-center justify-between mt-6">
              <button type="button" onClick={() => setRemember(!remember)} data-testid="login-remember" className="flex items-center gap-2.5">
                <span className={`w-4 h-4 border ${remember ? "bg-lux-ink border-lux-ink" : "border-lux-lineStrong bg-white"}`} />
                <span className="text-[13px] text-lux-sec">Remember me</span>
              </button>
              <span data-testid="login-forgot" className="text-[11px] tracking-[0.15em] uppercase text-lux-accent">Forgot password</span>
            </div>
            {err ? <p className="text-[#A33A2A] text-[13px] mt-4" data-testid="login-err">{err}</p> : null}
            <div className="h-6" />
            <ButtonLux label={loading ? "Signing in…" : "Sign In"} onClick={submitCreds} disabled={loading} testId="login-submit" />
          </>
        ) : (
          <>
            <SectionLabel>Two-Factor</SectionLabel>
            <h1 className="font-serif text-lux-ink font-light tracking-tight leading-tight mt-2 text-4xl md:text-6xl">Verify it’s you.</h1>
            <p className="text-base text-lux-sec font-light mt-4">Open your authenticator app and enter the 6-digit code generated for Onedot6.</p>
            <div className="h-8" />
            <label className="block text-[11px] tracking-[0.15em] uppercase text-lux-muted mb-2">Authentication Code</label>
            <input data-testid="login-2fa-code" value={code} onChange={(e) => setCode(e.target.value.slice(0, 12))} placeholder="6-digit code OR recovery code" onKeyDown={(e) => e.key === "Enter" && submitCode()} className={`${inputCls} tracking-[0.5em] text-xl`} />
            {err ? <p className="text-[#A33A2A] text-[13px] mt-4" data-testid="login-err">{err}</p> : null}
            <p className="text-[13px] text-lux-sec mt-4 font-light">Lost your authenticator? Enter one of your recovery codes instead. Each can be used once.</p>
            <div className="h-6" />
            <div className="flex items-center gap-4">
              <ButtonLux label={loading ? "Verifying…" : "Verify"} onClick={submitCode} disabled={loading} testId="login-2fa-submit" />
              <button type="button" onClick={() => { setStep("creds"); setErr(null); setCode(""); }} data-testid="login-2fa-back" className="text-[11px] tracking-[0.15em] uppercase text-lux-accent">Use different account</button>
            </div>
          </>
        )}
      </Container>
      <Footer />
    </div>
  );
}
